import React, { useEffect, useState } from "react";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Box,
  Chip,
  Button,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import CkEditor from "../../../components/CkEditor/CkEditor";
import {
  addEndClientsAsync,
  editEndClientsAsync,
  getHiringmanagersAsync,
} from "../../../_redux/_services/ClientsSlice";
import "./EndClientForm.css";

const EndClientForm = ({ mode, data, clientId, onClose }) => {
  const dispatch = useDispatch();
  const [hiringManagersList, setHiringManagersList] = useState([]);

  const hiringManagersData = useSelector(
    (state) => state.GetHiringManagers.data?.data,
  );

  useEffect(() => {
    if (clientId) {
      dispatch(
        getHiringmanagersAsync(clientId, {
          limit: 100,
          offset: 1,
          keyword: "",
        }),
      );
    }
  }, [dispatch, clientId]);

  useEffect(() => {
    if (hiringManagersData) {
      setHiringManagersList(hiringManagersData?.rows || []);
    }
  }, [hiringManagersData]);

  const validationSchema = Yup.object({
    name: Yup.string().required("End Client Name is required"),
    location: Yup.string().required("Location is required"),
    status: Yup.number().required("Status is required"),
    websiteURL: Yup.string().matches(
      /^(https?:\/\/)?(www\.)[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-z]{2,20}(\/.*)?$/,
      "Please enter a valid website URL",
    ),
  });

  const formik = useFormik({
    initialValues: {
      name: data?.name || "",
      location: data?.location || "",
      status: data?.status !== undefined ? data.status : 1,
      websiteURL: data?.websiteURL || "",
      hiringManager: data?.hiringManagers?.map((m) => String(m.id)) || [],
      notes: data?.notes || "",
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const payload = {
        ...values,
      };
      if (mode === "add") {
        await dispatch(addEndClientsAsync(clientId, payload));
      } else {
        await dispatch(editEndClientsAsync(clientId, payload, data.id));
      }
      onClose(true);
    },
  });

  const handleCkEditorUpdate = (obj) => {
    formik.setFieldValue("notes", obj.data);
  };

  const storedTheme = JSON.parse(localStorage.getItem("themeSettings")) || "";

  return (
    <div
      className="end-client-form-container"
      style={{
        "--primary-bg-color": storedTheme?.primaryColor,
        "--primary-font-color": storedTheme?.primaryFontColor,
      }}
    >
      <form onSubmit={formik.handleSubmit}>
        <div className="form-body">
          <div className="form-row">
            <div className="form-group">
              <TextField
                fullWidth
                label="End Client Name*"
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                InputLabelProps={{ shrink: true }}
              />
            </div>
            <div className="form-group">
              <TextField
                fullWidth
                label="Location*"
                name="location"
                value={formik.values.location}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.location && Boolean(formik.errors.location)
                }
                helperText={formik.touched.location && formik.errors.location}
                InputLabelProps={{ shrink: true }}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <FormControl fullWidth>
                <InputLabel shrink>Status</InputLabel>
                <Select
                  sx={{ textAlign: "left" }}
                  name="status"
                  value={formik.values.status}
                  onChange={formik.handleChange}
                  input={<OutlinedInput label="Status" notched />}
                >
                  <MenuItem value={1}>Active</MenuItem>
                  <MenuItem value={0}>Inactive</MenuItem>
                </Select>
              </FormControl>
            </div>
            <div className="form-group">
              <TextField
                fullWidth
                label="Website"
                name="websiteURL"
                value={formik.values.websiteURL}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.websiteURL && Boolean(formik.errors.websiteURL)
                }
                helperText={
                  formik.touched.websiteURL && formik.errors.websiteURL
                }
                InputLabelProps={{ shrink: true }}
              />
            </div>
          </div>

          <div className="form-row full-width" style={{ width: "48%" }}>
            <div className="form-group">
              <FormControl fullWidth>
                <InputLabel shrink>Hiring Manager</InputLabel>
                <Select
                  multiple
                  name="hiringManager"
                  value={formik.values.hiringManager}
                  onChange={formik.handleChange}
                  input={<OutlinedInput label="Hiring Manager" notched />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => {
                        const manager = hiringManagersList.find(
                          (m) => String(m.id) === value,
                        );
                        return (
                          <Chip
                            key={value}
                            label={manager ? manager.fullName : value}
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {hiringManagersList.map((manager) => (
                    <MenuItem key={manager.id} value={String(manager.id)}>
                      {manager.fullName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>

          <div className="form-row full-width">
            <div className="form-group">
              <label className="ck-label">End Client Notes</label>
              <CkEditor
                data={formik.values.notes}
                onUpdatehandler={handleCkEditorUpdate}
              />
            </div>
          </div>
        </div>

        <div className="form-footer">
          <Button className="cancel-btn" onClick={() => onClose(false)}>
            Cancel
          </Button>
          <Button className="save-btn" type="submit" variant="contained">
            {mode === "add" ? "Save" : "Update"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EndClientForm;
